import { z } from 'zod';

import { UnidentifiedSchema } from './sbc-customer';

export const PROMO_CODE_NOT_IN_CONVERSATION = 'code not in the conversation';

export const REFERRAL_LINK_BASE = 'https://www.seoulbeautyclub.com/i/';

export const PROMO_CODE_TYPES = [
  'sbc_promo_campaign',
  'creator_code',
  'referral_code',
  'stripe_promotion_code',
  'stripe_coupon',
  'gift_card',
] as const;
export type PromoCodeType = (typeof PROMO_CODE_TYPES)[number];

export const PROMO_CODE_STATUSES = [
  'active',
  'inactive',
  'expired',
  'max_redemptions_reached',
] as const;
export type PromoCodeStatus = (typeof PROMO_CODE_STATUSES)[number];

export const PromoDiscountSchema = z.object({
  percentOff: z.number().nullable(),
  amountOffCents: z.number().nullable(),
  currency: z.string().nullable(),
  duration: z.string().nullable(),
  durationInMonths: z.number().nullable(),
  addOnCreditCents: z.number().nullable(),
  addOnCreditExpiryDays: z.number().nullable(),
});
export type PromoDiscount = z.infer<typeof PromoDiscountSchema>;

export const PromoConditionsSchema = z.object({
  newMembersOnly: z.boolean(),
  returningMembersOnly: z.boolean(),
  firstBoxOnly: z.boolean().nullable(),
  minimumAmountCents: z.number().nullable(),
  deliveryPlans: z.string().nullable(),
  boxTypes: z.array(z.string()).nullable(),
  replacesPrepaidDiscount: z.boolean().nullable(),
  restrictedTo: z.enum(['this_member', 'another_member']).nullable(),
  combinable: z.string(),
});
export type PromoConditions = z.infer<typeof PromoConditionsSchema>;

export const PromoValiditySchema = z.object({
  status: z.enum(PROMO_CODE_STATUSES),
  expiresAt: z.string().nullable(),
  maxRedemptions: z.number().nullable(),
});
export type PromoValidity = z.infer<typeof PromoValiditySchema>;

export const PromoMemberUseSchema = z.object({
  source: z.string(),
  at: z.string().nullable(),
});
export type PromoMemberUse = z.infer<typeof PromoMemberUseSchema>;

export const PromoMemberUsageSchema = z.union([
  z.object({
    checked: z.literal(true),
    used: z.boolean(),
    uses: z.array(PromoMemberUseSchema),
    stripeChecked: z.boolean(),
  }),
  z.object({ checked: z.literal(false), reason: z.string() }),
]);
export type PromoMemberUsage = z.infer<typeof PromoMemberUsageSchema>;

export const PromoCodeResultSchema = z.union([
  z.object({
    available: z.literal(true),
    code: z.string(),
    exists: z.boolean().nullable(),
    type: z.enum(PROMO_CODE_TYPES).nullable(),
    useTool: z.string().nullable(),
    name: z.string().nullable(),
    discount: PromoDiscountSchema.nullable(),
    conditions: PromoConditionsSchema.nullable(),
    validity: PromoValiditySchema.nullable(),
    ownedByMember: z.boolean().nullable(),
    memberUsage: PromoMemberUsageSchema.nullable(),
    stripeChecked: z.boolean(),
    notes: z.array(z.string()),
  }),
  z.object({ available: z.literal(false), reason: z.string() }),
]);
export type PromoCodeResult = z.infer<typeof PromoCodeResultSchema>;

export const REFERRAL_CREDIT_STATUSES = [
  'available',
  'used',
  'expired',
  'removed',
  'pending',
  'applied',
  'granted',
  'pending_stock',
  'reversed',
] as const;

export const ReferralCreditSchema = z.object({
  kind: z.enum(['add_on_credit', 'store_credit', 'free_box']),
  source: z.enum(['referral', 'starter_referral']),
  amountCents: z.number().nullable(),
  remainingCents: z.number().nullable(),
  status: z.enum(REFERRAL_CREDIT_STATUSES),
  grantedAt: z.string().nullable(),
  expiresAt: z.string().nullable(),
});
export type ReferralCredit = z.infer<typeof ReferralCreditSchema>;

export const ReferredFriendSchema = z.object({
  friend: z.string().nullable(),
  status: z.enum(['signed_up', 'first_box_paid']),
  signedUpAt: z.string().nullable(),
  firstBoxPaidAt: z.string().nullable(),
  starterTrialStatus: z.string().nullable(),
  creditForHer: ReferralCreditSchema.nullable(),
});
export type ReferredFriend = z.infer<typeof ReferredFriendSchema>;

export const ReferralsResultSchema = z.union([
  z.object({
    identified: z.literal(true),
    referralCode: z.string().nullable(),
    referralLink: z.string().nullable(),
    friends: z.array(ReferredFriendSchema),
    credits: z.array(ReferralCreditSchema),
    availableAddOnCreditCents: z.number(),
    pendingStoreCreditCents: z.number(),
  }),
  UnidentifiedSchema,
]);
export type ReferralsResult = z.infer<typeof ReferralsResultSchema>;
